import Navbar from "@/components/common/navbar";
import OtherPageBg from "@/components/common/other-hero";
import Footer from "@/components/layout/footer";
import { locales } from "@/locales";
import Head from "next/head";
import { useRouter } from "next/router";

const AboutPage = ({ locale }: { locale: string; }) => {
  const router = useRouter();
  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals in the United States",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/about",
          name: "About",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>About Us - Rent80</title>
        <meta
          name="description"
          content="Rent80 compare the rental car prices from all major car rental providers such as sixt, avis, dollar, budget and kayak. Rent80 great selection and prices on rental cars."
        />
        <link
          rel="alternate"
          href="https://www.rent80.com/about"
          hrefLang="en-US"
        />
        {locales.map((data) => (
          <link
            rel="alternate"
            hrefLang={data.code}
            href={`https://www.rent80.com/${data.code}/about`}
            key={data.code}
          />
        ))}
        {typeof window !== "undefined" && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchemaJSON)}
          </script>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta
          property="og:title"
          content={`Rent80 - Find the cheap car rental deals and rental cars.`}
        />
        <meta
          property="og:description"
          content={`Rent80 compare the rental car prices from all major car rental providers such as sixt, avis, dollar, budget and kayak. Rent80 great selection and prices on rental cars.`}
        />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />
        <meta property="og:url" content={`https://www.rent80.com/about`} />
        <link
          rel="alternate"
          href="https://www.rent80.com/about"
          hrefLang="x-default"
        />
        {locale && (
          <link
            rel="canonical"
            href={`https://www.rent80.com${locale === "en" ? "" : `/${locale}`}/about`}
          />
        )}
        {!locale && router && (
          <link rel="canonical" href={`https://www.rent80.com/${router.locale}/about`} />
        )}
      </Head>
      <main className="min-h-screen">
        <div className="lg:relative">
          <Navbar bg={true} infoPage={true} />
          <OtherPageBg />
        </div>
        <div className="w-full max-w-7xl mx-auto px-[13px] my-[40px]">
          <h1 className="max-w-[300px] font-extrabold lg:font-bold text-[18px] lg:text-[22px] text-my-dark-blue lg:max-w-[345px] mb-[40px]">
            About us
          </h1>

          <p className="text-[20px] text-my-light-blue pb-[64px]">
            Rent80 is one of the biggest rental car providers in the world, with
            a presence in over 80 countries. We offer convenient, affordable,
            and flexible rental car solutions that are customized to meet the
            needs of our customers. Our goal is to provide a satisfying rental
            experience that is both cost-effective and meets the customer&apos;s
            specific requirements.
          </p>

          <p className="text-[20px] text-my-light-blue pb-[64px]">
            Rent80 is the trusted partner of Kayak, Avis, Sixt, Budget, and
            Dollar. We have integrated with these giants of the rental car
            industry to provide you with the best possible selection of rental
            cars. Whether you need a small economy car for a city break or a
            larger SUV for a family holiday, we have the perfect rental solution
            for you.
          </p>

          <p className="text-[20px] text-my-light-blue pb-[64px]">
            So why choose Rent80 for your rental car needs? We believe that our
            combination of convenience, affordability, and flexibility makes us
            the perfect choice for anyone looking for a rental car. We also
            offer a wide range of services that can be customized to meet your
            specific needs. So why not give us a try today and see for yourself?
            We&apos;re sure you won&apos;t be disappointed.
          </p>
        </div>
        <Footer />
      </main>
    </>
  );
};

export async function getStaticProps() {
  return {
    props: {
      messages: (await import("../messages/en-US.json")).default,
    },
  };
}

export default AboutPage;
